import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function formatDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
}

function formatMoney(minorUnits, currency) {
  const value = Number(minorUnits || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || 'USD'}`;
  }
}

function formatLots(value, fallback = '0.00') {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  const num = Number(value);
  if (!Number.isFinite(num)) {
    return String(value);
  }

  return num.toFixed(2);
}

function TradesTable({
  trades,
  total,
  totalLots,
  totalProfit,
  profitCurrency,
  isLoading,
  error,
  showAccountLogin,
  symbolFilter,
  dateFromFilter,
  dateToFilter,
  onSymbolChange,
  onDateFromChange,
  onDateToChange,
  onLoadMore,
  hasMore,
}) {
  const { t } = useTranslation();
  const [localSymbol, setLocalSymbol] = useState(symbolFilter || '');
  const debounceRef = useRef(null);

  useEffect(() => {
    setLocalSymbol(symbolFilter || '');
  }, [symbolFilter]);

  const handleSymbolChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalSymbol(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        onSymbolChange(value.trim());
      }, 500);
    },
    [onSymbolChange],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const profitNum = Number(totalProfit || 0);
  const profitColorClass = profitNum >= 0 ? 'text-success' : 'text-danger';

  return (
    <div>
      {/* Filters */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <label className="form-label">{t('trades.symbol')}</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. EURUSD"
            value={localSymbol}
            onChange={handleSymbolChange}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">{t('trades.dateFrom')}</label>
          <input
            type="date"
            className="form-control"
            value={dateFromFilter || ''}
            onChange={(e) => onDateFromChange(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">{t('trades.dateTo')}</label>
          <input
            type="date"
            className="form-control"
            value={dateToFilter || ''}
            onChange={(e) => onDateToChange(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="card border-0 bg-light">
            <div className="card-body text-center py-2">
              <div className="text-muted small">{t('trades.totalTrades')}</div>
              <div className="fw-bold fs-5">{total}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 bg-light">
            <div className="card-body text-center py-2">
              <div className="text-muted small">{t('trades.totalVolume')}</div>
              <div className="fw-bold fs-5">{formatLots(totalLots)}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 bg-light">
            <div className="card-body text-center py-2">
              <div className="text-muted small">{t('trades.netPL')}</div>
              <div className={`fw-bold fs-5 ${profitColorClass}`}>
                {formatMoney(totalProfit, profitCurrency)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* Loading */}
      {isLoading && trades.length === 0 ? (
        <div className="text-muted">{t('trades.loadingTrades')}</div>
      ) : null}

      {/* Table */}
      {!isLoading && trades.length === 0 ? (
        <div className="text-muted">{t('trades.noTradesFound')}</div>
      ) : null}

      {trades.length > 0 ? (
        <div className="table-responsive">
          <table className="table search-table align-middle text-nowrap mb-0">
            <thead className="header-item">
              <tr>
                {showAccountLogin ? <th>{t('trades.account')}</th> : null}
                <th>{t('trades.symbol')}</th>
                <th>{t('trades.volume')}</th>
                <th>{t('trades.openPrice')}</th>
                <th>{t('trades.closePrice')}</th>
                <th>{t('trades.pl')}</th>
                <th>{t('trades.closedAt')}</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade) => {
                const profit = Number(trade.profitAmount || 0);
                const plClass = profit >= 0 ? 'text-success' : 'text-danger';

                return (
                  <tr key={trade.uid || trade.externalId}>
                    {showAccountLogin ? <td>{trade.accountLogin || '-'}</td> : null}
                    <td>{trade.symbol || '-'}</td>
                    <td>{trade.lotsComputed === false ? '-' : formatLots(trade.lots, '-')}</td>
                    <td>{trade.openPrice || '-'}</td>
                    <td>{trade.closePrice || '-'}</td>
                    <td className={plClass}>
                      {formatMoney(trade.profitAmount, profitCurrency)}
                    </td>
                    <td>{formatDate(trade.closedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Load More */}
      {hasMore && !isLoading ? (
        <div className="text-center mt-3">
          <button type="button" className="btn btn-outline-primary" onClick={onLoadMore}>
            {t('trades.loadMore')}
          </button>
        </div>
      ) : null}

      {isLoading && trades.length > 0 ? (
        <div className="text-center mt-3 text-muted">{t('common.loading')}</div>
      ) : null}
    </div>
  );
}

export default TradesTable;
